import { useTranslation } from "react-i18next"
import { useIBState, useShouldShowIBportal } from "ib/utils/hooks"
import {
  useAccounts, useAppState,
  useIsGeneralSidebarEnabled, useOrganization,
  usePlatformAvailability, useShowThinkCapital, useThinkRewardsAvailable, useUserApplications
} from "../../../../utils/hooks"
import appRoutes from "../../../../utils/routes"
import { DashboardMenuItem, filterMenuItems } from "./utils"
import styles from "./style.module.scss"
import NewBadge from "../../../Presentational/NewBadge"

export const useMenuEntries = () => {
  const { t } = useTranslation()
  const { isTMJP, isTMCY, isUK, isAU, isTMNZ, isTMSY, isTMLC } = useOrganization()
  const { accountsLoaded, userReferrerStatus, appStatus, userProfile, redirectedFromWTR } = useAppState()
  const thinkRewardsAvailable = useThinkRewardsAvailable()
  const { hasLiveAccounts, isDemo, availabeForDeposit, availabeForTransfer,
    enabledLiveAccounts, liveAccounts, demoAccounts, hasLiveTT, hasLiveThinkCopy } = useAccounts()
  const shouldShowIBportal = useShouldShowIBportal()
  const { hasApprovedCFD, simplifiedAppNotApprovedYet } = useUserApplications()
  const { isThinkCopyPlatformEnabled, isPAMMPlatformEnabled } = usePlatformAvailability(hasLiveAccounts ? 'additional' : 'onboarding')
  const isGeneralSidebarEnabled = useIsGeneralSidebarEnabled()
  const { application: ibApplication } = useIBState()

  const fundsEnabled = () => {
    return accountsLoaded && (appStatus === 'APPROVED' || hasLiveAccounts)
  }

  const downloadsEnabled = () => {
    const hasRetailLiveAccounts = liveAccounts.some(x => x.platformAccountType !== 'Commission'
      && x.platformAccountType !== 'Investor')
    const hasDemoAccounts = demoAccounts.length > 0
    return accountsLoaded && (hasRetailLiveAccounts || hasDemoAccounts)
  }

  const showPartner = !(isTMJP || isUK || isAU || isTMNZ || isTMCY || isTMSY)
  //removed as per THPRT-5838
  const showThinkCapital = useShowThinkCapital()
  const shouldDisplayReferFriend = Boolean(
    !ibApplication &&
    (userReferrerStatus?.allowedToBeReferrer || userReferrerStatus?.existingReferrer)
  )

  const menuItems: DashboardMenuItem[] = [
    {
      name: "accounts",
      customCaption: t('dashboard.sidebar.dashboard'),
      icon: {
        active: '/images/sidebar/accounts.svg',
        inactive: '/images/sidebar/accounts.svg',
        disabled: '/images/sidebar/accounts.svg'
      },
      link: appRoutes.dashboard.accounts.root
    },
    {
      name: "funds",
      customCaption: t('dashboard.sidebar.funds.fundsManagement'),
      icon: {
        active: '/images/sidebar/funds.svg',
        inactive: '/images/sidebar/funds.svg',
        disabled: '/images/sidebar/funds.svg'
      },
      disabled: isDemo,
      children: [
        {
          name: "deposit",
          link: appRoutes.dashboard.funds.deposit,
          disabled: !fundsEnabled() || availabeForDeposit.length === 0,
        },
        {
          name: "withdraw",
          link: appRoutes.dashboard.funds.withdraw,
          // keepActiveAppearance: simplifiedAppNotApprovedYet,
          disabled: simplifiedAppNotApprovedYet || !fundsEnabled() || availabeForTransfer.length === 0
        },
        {
          name: "transfer",
          link: appRoutes.dashboard.funds.transfer,
          // keepActiveAppearance: simplifiedAppNotApprovedYet,
          disabled: simplifiedAppNotApprovedYet || !fundsEnabled() || availabeForTransfer.length === 0 || enabledLiveAccounts.length < 2
        },
        {
          name: "transaction_history",
          link: appRoutes.dashboard.funds.history,
          disabled: !fundsEnabled(),
        },
        {
          name: "bank_accounts",
          link: appRoutes.dashboard.funds.bankAccounts,
          disabled: !fundsEnabled(),
        },
        {
          name: "prepaid_card",
          customCaption: <div className={styles.new}>{t('dashboard.sidebar.funds.prepaid_card')}<NewBadge /></div>,
          link: appRoutes.dashboard.funds.prepaidCard,
          disabled: !fundsEnabled(),
          shouldDisplay: () => isTMLC
            && userProfile?.prepaidCardMenuVisible === true && !redirectedFromWTR,
        }
      ]
    },
    {
      name: "downloads",
      icon: {
        active: '/images/sidebar/platforms.svg',
        inactive: '/images/sidebar/platforms.svg',
        disabled: '/images/sidebar/platforms.svg'
      },
      disabled: !downloadsEnabled(),
      link: appRoutes.dashboard.downloads
    },
    {
      name: "promotions",
      icon: {
        active: '/images/sidebar/promotions.svg',
        inactive: '/images/sidebar/promotions.svg',
        disabled: '/images/sidebar/promotions.svg'
      },
      shouldDisplay: () => shouldDisplayReferFriend,
      children: [
        {
          name: "refer_a_friend",
          customCaption: t('dashboard.sidebar.refer_a_friend'),
          link: appRoutes.dashboard.referAFriend,
          shouldDisplay: () => shouldDisplayReferFriend,
        },
      ]
    },
    {
      name: "thinkCopy",
      icon: {
        active: '/images/sidebar/CopyTrading.svg',
        inactive: '/images/sidebar/CopyTrading.svg',
        disabled: '/images/sidebar/CopyTrading.svg'
      },
      customCaption: t('dashboard.sidebar.thinkCopy'),
      link: appRoutes.dashboard.thinkCopy,
      // Show for anyone who can create a ThinkCopy account (platform published) OR who already
      // owns a LIVE ThinkCopy account, so existing holders keep access even when the org config no
      // longer publishes THINK_COPY (e.g. TMLC/AU prod gap). Live-only, so a demo-only owner does
      // not land on the page's empty live-account table.
      shouldDisplay: () => isThinkCopyPlatformEnabled || hasLiveThinkCopy,
    },
    {
      name: "pamm",
      icon: {
        active: '/images/sidebar/pamm.svg',
        inactive: '/images/sidebar/pamm.svg',
        disabled: '/images/sidebar/pamm.svg'
      },
      customCaption: <div className={styles.new}>{t('dashboard.sidebar.pamm')}<NewBadge /></div>,
      link: appRoutes.dashboard.pamm,
      shouldDisplay: () => isPAMMPlatformEnabled && hasLiveAccounts && !isTMJP
    },
    {
      name: "tools",
      customCaption: t('dashboard.sidebar.tools.tradingServices'),
      icon: {
        active: '/images/sidebar/tools.svg',
        inactive: '/images/sidebar/tools.svg',
        disabled: '/images/sidebar/tools.svg'
      },
      disabled: !isGeneralSidebarEnabled,
      shouldDisplay: () => !isTMJP,
      children: [
        {
          name: "traders_gym",
          link: appRoutes.dashboard.tools.traders_gym,
          shouldDisplay: () => isDemo || hasApprovedCFD
        },
        {
          name: "economic_calendal",
          link: appRoutes.dashboard.tools.economic_calendar,
        },
      ]
    },
    {
      name: "partnerships",
      icon: {
        active: '/images/sidebar/team.svg',
        inactive: '/images/sidebar/team.svg',
        disabled: '/images/sidebar/team.svg'
      },
      shouldDisplay: () => showPartner,
      link: appRoutes.dashboard.partnerships
    },
    {
      name: "loyalty_program",
      icon: {
        active: '/images/sidebar/loyalty-program.svg',
        inactive: '/images/sidebar/loyalty-program.svg',
        disabled: '/images/sidebar/loyalty-program.svg'
      },
      shouldDisplay: () => thinkRewardsAvailable,
      link: appRoutes.dashboard.thinkRewards
    },
    {
      name: "thinkcapital",
      icon: {
        active: '/images/sidebar/tcapital.png',
        inactive: '/images/sidebar/tcapital.png',
        disabled: '/images/sidebar/tcapital.png'
      },
      shouldDisplay: () => showThinkCapital,
      link: appRoutes.dashboard.thinkCapital
    },

    {
      name: "support",
      icon: {
        active: '/images/sidebar/support.svg',
        inactive: '/images/sidebar/support.svg',
        disabled: '/images/sidebar/support.svg'
      },
      children: [
        {
          name: "supportRequest",
        },
        {
          name: "faq",
          matchLinks: [appRoutes.dashboard.support.faq]
        },
      ]
    },
    {
      name: "subscription",
      icon: {
        active: '/images/sidebar/subscription.svg',
        inactive: '/images/sidebar/subscription.svg',
        disabled: '/images/sidebar/subscription.svg'
      },
      disabled: !isGeneralSidebarEnabled,
      shouldDisplay: () => Boolean(isTMJP && hasLiveTT),
      link: appRoutes.dashboard.subscription
    },
    {
      name: "logout",
      icon: {
        active: '/images/sidebar/logout.svg',
        inactive: '/images/sidebar/logout.svg',
        disabled: '/images/sidebar/logout.svg'
      },
    },
    {
      name: 'separator',
      shouldDisplay: () => shouldShowIBportal
    },
    {
      name: "ib",
      icon: {
        active: '/images/sidebar/ib.svg',
        inactive: '/images/sidebar/ib.svg',
        disabled: '/images/sidebar/ib.svg'
      },
      shouldDisplay: () => shouldShowIBportal
    },
  ]

  return filterMenuItems(menuItems)
}
